import { isDefined } from "@clipboard-health/util-ts";
import { Platform } from "@src/mobile/utils/platform";
import {
  addPushNotificationActionPerformedListener,
  type PushNotificationActionPerformed,
} from "@src/mobile/utils/pushNotifications.adapter";

type NotificationTapHandler = (action: PushNotificationActionPerformed) => void;

const pendingTapQueue: PushNotificationActionPerformed[] = [];
let tapHandler: NotificationTapHandler | undefined;

/**
 * Module-scoped listener for push notification taps.
 *
 * Registered at import time (before React mounts) to capture cold-start
 * notification taps that fire before the handler hook is ready.
 * Events are queued until `setNotificationTapHandler` is called.
 */
if (Platform.isMobile) {
  void addPushNotificationActionPerformedListener((action) => {
    if (isDefined(tapHandler)) {
      tapHandler(action);
    } else {
      pendingTapQueue.push(action);
    }
  });
}

/**
 * Set the handler for notification taps.
 * Drains any queued events from before the handler was ready,
 * then routes future events directly to the handler.
 */
export function setNotificationTapHandler(handler: NotificationTapHandler): void {
  for (const action of pendingTapQueue) {
    try {
      handler(action);
    } catch {
      // Swallow errors from individual queued events so the remaining
      // events are still drained and the handler is assigned.
    }
  }
  pendingTapQueue.length = 0;
  tapHandler = handler;
}

/**
 * Remove the current tap handler.
 * Future events will be queued again until a new handler is set.
 */
export function clearNotificationTapHandler(): void {
  tapHandler = undefined;
}

/**
 * Reset queue state. Only for use in tests.
 */
export function resetNotificationTapQueue(): void {
  pendingTapQueue.length = 0;
  tapHandler = undefined;
}
